import { api } from './api';

export interface StatDelta {
  value: number;
  previous: number;
  deltaPct: number | null;
}

export interface DashboardResponse {
  period: { start: string; end: string; previousStart: string; previousEnd: string };
  stats: {
    ongoingOpportunities: StatDelta;
    /** CA prévisionnel PONDÉRÉ (Σ weighted_total_revenue). */
    weightedRevenueTotal: StatDelta;
    /** CA prévisionnel NON pondéré (Σ total_revenue) — toggle « Pondération ». */
    unweightedRevenueTotal: StatDelta;
    quotesEmittedInPeriod: StatDelta;
    signedInPeriod: StatDelta;
    conversionRatePct: StatDelta;
  };
  revenueByMonth: Array<{
    month: string;
    /** CA total (com + frais + honoraires). */
    revenue: number;
    /** Sub-info breakdown — décision Q A 2026-04-30. */
    commissionRevenue: number;
    feesAndHonorairesRevenue: number;
    previousYearRevenue: number;
    previousCommissionRevenue: number;
    previousFeesAndHonorairesRevenue: number;
  }>;
  contractTypeBreakdown: Array<{
    contractTypeId: string;
    code: string;
    label: string;
    count: number;
    /** Σ weighted_total_revenue (com + frais + honoraires pondéré). */
    weightedRevenue: number;
    /** Sub-info breakdown — décision 2026-04-30. */
    weightedCommissionRevenue: number;
    weightedFeesAndHonorairesRevenue: number;
  }>;
  // (le shape ci-dessus est déjà aligné avec le backend - laissé tel quel)
  activityByWeek: Array<{
    week: string;
    call: number;
    meeting: number;
    task: number;
    email: number;
    sms: number;
    other: number;
  }>;
  alerts: {
    stalledQuotes: Array<{
      id: string;
      reference: string;
      prospectName: string;
      stageChangedAt: string;
    }>;
    staleOpportunities: Array<{
      id: string;
      reference: string;
      prospectName: string;
      lastActivityAt: string | null;
    }>;
    /** Top 10 opps non terminales par commission_amount DESC (brutes,
     *  non pondérées par le score). */
    topCommissions: Array<{
      id: string;
      reference: string;
      prospectName: string;
      commissionAmount: number;
      stage: string;
    }>;
    avgConversionDays: number | null;
  };
}

export interface FunnelResponse {
  period: { start: string; end: string };
  stages: Array<{
    stage: string;
    count: number;
    weightedRevenue: number;
    conversionFromPreviousPct: number | null;
  }>;
  overallConversionPct: number;
  totalCreated: number;
}

export type Granularity = 'day' | 'week' | 'month';

export interface KpiFilters {
  period_start?: string;
  period_end?: string;
  /** Granularité des buckets temporels (date_trunc côté backend). Le frontend
   *  calcule via `computeGranularity()` selon la longueur de la plage. Absent
   *  = backend utilise `month` par défaut (rétrocompat). */
  granularity?: Granularity;
  /** Pivot de date du dashboard (décision 2026-07-03) : `created` (défaut,
   *  chaque card garde son pivot propre) ou `effective` (toggle « Date d'effet » :
   *  toutes les cards pilotées par la période filtrent sur `effective_date`). */
  date_pivot?: 'created' | 'effective';
  user_id?: string;
  team_id?: string;
  contract_type_id?: string;
  company_id?: string;
}

/**
 * Règle V1 : `< 3 semaines → jours`, `3 semaines à 3 mois → semaines`,
 * `> 3 mois → mois`. Évite les graphs temporels vides sur des petites plages
 * (un graph groupé par mois sur 10 jours = un seul point, inutilisable).
 *
 * Ordre des conditions : jour en premier (plus restrictif) puis semaine.
 */
export function computeGranularity(fromIso: string, toIso: string): Granularity {
  const diffMs = new Date(toIso).getTime() - new Date(fromIso).getTime();
  const diffDays = diffMs / 86_400_000;
  if (diffDays <= 21) return 'day';
  if (diffDays <= 92) return 'week';
  return 'month';
}

function qs(filters: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v === undefined || v === null || v === '') continue;
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

export const kpiApi = {
  dashboard: (token: string, filters: KpiFilters = {}) =>
    api.get<DashboardResponse>(`/api/v1/kpi/dashboard${qs(filters as Record<string, unknown>)}`, {
      accessToken: token,
    }),
  funnel: (token: string, filters: KpiFilters = {}) =>
    api.get<FunnelResponse>(`/api/v1/kpi/funnel${qs(filters as Record<string, unknown>)}`, {
      accessToken: token,
    }),
};

export type PeriodPreset =
  | 'week'
  | 'month'
  | 'quarter'
  | 'year'
  | 'last12months'
  // Presets « futurs » - utilisés par la carte Trésorerie de KPI Direction
  // pour visualiser l'encaissement à venir. Pas exposés dans le filtre
  // principal de page (qui ne concerne que les KPIs sur du passé).
  | 'next6months'
  | 'next12months'
  | 'spanLastNext12months';

export function presetPeriod(preset: PeriodPreset): {
  period_start: string;
  period_end: string;
} {
  // /!\ TIMEZONE - on construit toutes les bornes en UTC via `Date.UTC` puis
  // sérialise via `toISOString`. Sinon `new Date(year, 0, 1)` crée un midnight
  // LOCAL → converti en ISO ça décale en arrière (ex. à Paris UTC+1, le 1er
  // janv. local = 31 déc 23:00 UTC). Côté backend, `date_trunc('month', ...)`
  // sur ce timestamp UTC retournerait décembre au lieu de janvier - bug
  // visible sur tous les charts qui font du raw SQL avec `date_trunc`.
  const now = new Date();
  const end = now;
  const year = now.getFullYear();
  const month = now.getMonth();
  let start: Date;
  if (preset === 'week') {
    // Lundi de la semaine en cours, anchored UTC à 00:00.
    const day = now.getDay() || 7;
    start = new Date(Date.UTC(year, month, now.getDate() - (day - 1)));
  } else if (preset === 'month') {
    start = new Date(Date.UTC(year, month, 1));
  } else if (preset === 'quarter') {
    const q = Math.floor(month / 3);
    start = new Date(Date.UTC(year, q * 3, 1));
  } else if (preset === 'last12months') {
    // 12 mois calendaires incluant le mois courant : ex. avril 2026 →
    // de mai 2025 (1er) à aujourd'hui.
    start = new Date(Date.UTC(year, month - 11, 1));
  } else if (preset === 'next6months') {
    start = new Date(Date.UTC(year, month, 1));
    // Override end à +6 mois
    return {
      period_start: start.toISOString(),
      period_end: new Date(Date.UTC(year, month + 6, 0, 23, 59, 59, 999)).toISOString(),
    };
  } else if (preset === 'next12months') {
    start = new Date(Date.UTC(year, month, 1));
    return {
      period_start: start.toISOString(),
      period_end: new Date(Date.UTC(year, month + 12, 0, 23, 59, 59, 999)).toISOString(),
    };
  } else if (preset === 'spanLastNext12months') {
    start = new Date(Date.UTC(year, month - 11, 1));
    return {
      period_start: start.toISOString(),
      period_end: new Date(Date.UTC(year, month + 12, 0, 23, 59, 59, 999)).toISOString(),
    };
  } else if (preset === 'year') {
    // Trésorerie (pivot DATE D'EFFET) : « Cette année » = 02 janvier N au
    // 01 janvier N+1 inclus (convention « année d'effet » — décision 2026-06-28).
    // Distinct du « Cette année » Dashboard/Pipeline (01/01 → 31/12, basé sur la
    // date de création/signature, cf date-presets.ts). Ce resolver n'est appelé
    // QUE par la card Trésorerie de KPI Direction → aucun autre impact.
    return {
      period_start: new Date(Date.UTC(year, 0, 2)).toISOString(),
      period_end: new Date(Date.UTC(year + 1, 0, 1, 23, 59, 59, 999)).toISOString(),
    };
  } else {
    start = new Date(Date.UTC(year, 0, 1));
  }
  return { period_start: start.toISOString(), period_end: end.toISOString() };
}

// Refonte 2026-05-21 : 10 stages au total (8 PIPELINE_ORDER + 2 terminaux).
// Cohérent avec STAGE_LABELS dans `src/client/services/opportunities.api.ts`
// — duplication assumée car ce dictionnaire est aussi consommé par les
// charts KPI Direction qui parlent du funnel "weighted_revenue" agrégé,
// distinct des libellés Kanban (mais textuellement identiques).
export const STAGE_LABELS: Record<string, string> = {
  suspect: 'Suspect',
  contacted: 'Prise de contact',
  meeting_booked: 'RDV pris',
  meeting_done: 'RDV effectué',
  awaiting_client_docs: 'Attente éléments clients',
  awaiting_quote_supplier: 'Attente devis Compagnie',
  quote_sent: 'Devis émis',
  won: 'Signée',
  lost: 'Perdue',
  no_follow_up: 'Sans suite',
};
